import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { localStorageKeys } from '../../../constants/localStorageKeys.const';
import { PlansCtaTypeEnum } from '../../../enums/plans-cta-type.enum';
import PlanChangeModal from '../PlanChangeModal/PlanChangeModal.js';
// import PlansSlider from '../PlansSlider/PlansSlider';
import styles from './SubscriptionPlanList.module.scss';
import { translateUserSubscription } from '../../../helpers/translate-user-subscription';
import SubscriptionsService from '../../../services/subscriptions/subscriptions.service';
import { getMe } from '../../../store/actions/user.actions';
import { selectCurrentPlan, selectPlans, setIsShowGlobalLoader } from '../../../store/slices/global.slice';
import { PaymentProviders } from '../../../enums/payment-providers.enum';
import PaypalService from '../../../services/subscriptions/paypal.service';
import { getPlanPriceIdByEnvHelper } from '../../../helpers/get-plan-price-id-by-env.helper';
import { useHistory } from 'react-router-dom';
import ModalWrapper from '../../Modals/ModalWrapper/ModalWrapper';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Icon from '../Icon/Icon';
import { paymentMethodsData, showPaymentMethodModalStatuses } from './SubscriptionPlanList.const';
import classnames from 'classnames';
import { SubscriptionPlanListModalTypesEnum } from './SubscriptionPlanListModalTypes.enum';
import { CreditsRolloverModesEnum } from '../../../enums/credits-rollover-modes.enum';
import { creditsBankMaxCounter } from '../../../helpers/credits-bank-max-counter.helper';

const SubscriptionPlanList = ({
                                user,
                                isTrial = false,
                                finishTrial,
                                stripePromise,
                                paymentsProvider,
                                isPlanSelectionModal
                              }) => {
  const {
    currentPrice,
    isAnnual,
    tierNumber,
    nextBillingDate
  } = translateUserSubscription(user);

  const dispatch = useDispatch();
  const history = useHistory();
  const plans = useSelector(selectPlans, shallowEqual);
  const currentPlan = useSelector(selectCurrentPlan, shallowEqual);
  const [showModal, setShowModal] = useState(false);
  const [newPlan, setNewPlan] = useState(null);
  const [modalType, setModalType] = useState(SubscriptionPlanListModalTypesEnum.DOWNGRADE);
  const [rolloverMode, setRolloverMode] = useState(null);
  const [isPaymentMethodModalShown, setIsPaymentMethodModalShown] = useState(false);
  const [isCreditsBankWarningShown, setIsCreditsBankWarningShown] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(PaymentProviders.STRIPE);
  const { paypalPayments } = useFlags();

  const mappedPlans = useMemo(() => {
    if (!plans.length) {
      return;
    }
    return plans?.map(plan => {
      const { credits } = plan;
      const priceId = getPlanPriceIdByEnvHelper(plan, paymentsProvider);
      return {
        ...plan,
        credits,
        priceId
      };
    });
  }, [plans, paymentsProvider]);

  const openPlanChangeModal = () => {
    setShowModal(true);
  };

  const closePlanChangeModal = () => {
    setShowModal(false);
  };

  const setLocalStorageCurrentPlanId = () => localStorage.setItem(localStorageKeys.CURRENT_PLAN_ID, currentPrice);

  const selectPaymentMethod = method => () => setPaymentMethod(method);

  const redirectToPlanUrl = async planId => {
    dispatch(setIsShowGlobalLoader(true));
    try {
      const res = await SubscriptionsService.createCheckoutSession(planId);
      if (res.status === 200) {
        const stripe = await stripePromise;

        const { sessionId } = res.data;
        setLocalStorageCurrentPlanId();
        stripe
          .redirectToCheckout({
            sessionId
          })
          .finally();
      }
    } catch (err) {
      console.error(err);
      dispatch(setIsShowGlobalLoader(false));
    }
  };

  const downgradeStripePlan = async () => {
    dispatch(setIsShowGlobalLoader(true));
    try {
      const priceId = getPlanPriceIdByEnvHelper(newPlan, PaymentProviders.STRIPE);
      await SubscriptionsService.downgradeSubscription(priceId);
      setLocalStorageCurrentPlanId();
      history.push('/profile#billing?session_id=true');
      dispatch(getMe());
      dispatch(setIsShowGlobalLoader(false));
    } catch (err) {
      console.error(err);
      dispatch(setIsShowGlobalLoader(false));
    }
    closePlanChangeModal();
  };

  const showUpgradeModal = async plan => {
    await handleRolloverMode({ plan, isDowngrading: false });
  };

  const upgradePlan = async plan => {
    if (showPaymentMethodModalStatuses.includes(user.status) && paypalPayments) {
      setIsCreditsBankWarningShown(false);
      setIsPaymentMethodModalShown(true);
      return;
    }
    const priceId = getPlanPriceIdByEnvHelper(plan, paymentsProvider);
    if (paymentsProvider === PaymentProviders.PAYPAL) {
      dispatch(setIsShowGlobalLoader(true));
      const { url } = (await PaypalService.upgradeSubscription(priceId)).data;
      window.location.href = url;
      dispatch(setIsShowGlobalLoader(false));
      return;
    }
    await redirectToPlanUrl(priceId);
  };

  const joinFromModal = async () => {
    const priceId = getPlanPriceIdByEnvHelper(newPlan, paymentMethod);
    if (paymentMethod === PaymentProviders.PAYPAL) {
      dispatch(setIsShowGlobalLoader(true));
      const { url } = (await PaypalService.createPaypalSubscription(priceId)).data;
      window.location.href = url;
      dispatch(setIsShowGlobalLoader(false));
      return;
    }
    await redirectToPlanUrl(priceId);

  };

  const createPaypalSubscription = async priceId => {
    try {
      dispatch(setIsShowGlobalLoader(true));
      await PaypalService.createPaypalSubscription(priceId);
    } catch (e) {
      dispatch(setIsShowGlobalLoader(false));
      console.error(e);
    }
  };

  const downgradePlan = async plan => {
    const priceId = getPlanPriceIdByEnvHelper(plan, paymentsProvider);
    if (paymentsProvider === PaymentProviders.STRIPE) {
      await downgradeStripePlan();
    } else {
      setIsCreditsBankWarningShown(false);
      dispatch(setIsShowGlobalLoader(true));
      try {
        const { url } = (await PaypalService.downgradeSubscription(priceId)).data;
        window.location.href = url;
      } catch (e) {
        dispatch(setIsShowGlobalLoader(false));
      }
    }
  };

  const showDowngradeModal = async plan => {
    await handleRolloverMode({ plan, isDowngrading: true });
  };

  const handleRolloverMode = async ({ plan, isDowngrading }) => {
    if (!currentPlan) {
      await upgradePlan(plan);
      return;
    }
    if (isDowngrading) {
      if (paymentsProvider === PaymentProviders.PAYPAL) {
        setIsCreditsBankWarningShown(true);
      } else {
        await openPlanChangeModal(plan);
      }

      if (currentPlan.has_rollover && plan.has_rollover) {
        setRolloverMode(CreditsRolloverModesEnum.DOWNGRADE_WITH_ROLLOVER);
      } else if (currentPlan.has_rollover && !plan.has_rollover) {
        setRolloverMode(CreditsRolloverModesEnum.DOWNGRADE_WITHOUT_ROLLOVER);
      } else {
        setRolloverMode(null);
      }
    } else {
      if (currentPlan.has_rollover && !plan.has_rollover) {
        setRolloverMode(CreditsRolloverModesEnum.SWITCH);
        setIsCreditsBankWarningShown(true);
        return;
      }
      await upgradePlan(plan);
    }
  };

  const onCTAButtonClick = async ({ selectedPlan, ctaType }) => {
    setRolloverMode(null);
    setNewPlan(selectedPlan);
    const { name } = selectedPlan;

    switch (ctaType) {
      case PlansCtaTypeEnum.UPGRADE:
      case PlansCtaTypeEnum.SWITCH:
        TagManager.dataLayer({
          dataLayer: { event: 'upgradePlan', oldPlan: currentPlan, newPlan: name }
        });
        await showUpgradeModal(selectedPlan);
        break;
      case PlansCtaTypeEnum.NEW:
        TagManager.dataLayer({
          dataLayer: { event: 'newPlan', newPlan: name }
        });
        await showUpgradeModal(selectedPlan);
        break;
      case PlansCtaTypeEnum.DOWNGRADE:
        await showDowngradeModal(selectedPlan);
        break;
      default:
        return;
    }
  };

  return (
    <>
      <div className={styles.plans__body}>
        {/* <PlansSlider
          onCTAButtonClick={onCTAButtonClick}
          onTrialClick={finishTrial}
          plans={mappedPlans}
          currentPrice={currentPrice}
          isAnnual={isAnnual}
          isTrial={isTrial}
          tierNumber={tierNumber}
          paymentsProvider={paymentsProvider}
          isPlanSelectionModal={isPlanSelectionModal}
          stripePromise={stripePromise}
        /> */}
      </div>

      <PlanChangeModal
        appElement={document.getElementById('root')}
        isOpen={showModal}
        onRequestClose={closePlanChangeModal}
      >
        {newPlan && modalType === SubscriptionPlanListModalTypesEnum.DOWNGRADE && (
          <div className='downgradeModal'>
            <button className='downgradeModal__close' onClick={closePlanChangeModal}>
              Close
            </button>
            <h1 className='downgradeModal__title'>Confirm your new plan</h1>

            <>
              <p className='downgradeModal__currentHeading'>Changing to</p>
              <div className='downgradeModal__currentPlan'>
                <div className='downgradeModal__currentPlanLeft'>
                  <h4 className='downgradeModal__currentPlanTitle'> {newPlan?.title}</h4>
                  <p className='downgradeModal__currentPlanDescription'>
                    What you'll pay {newPlan?.isAnnual ? 'yearly' : 'monthly'} starting{' '}
                    <span>{moment(nextBillingDate).tz(user.timezone).format('MMMM DD YYYY')}</span>
                  </p>
                </div>
                <div className='downgradeModal__currentPlanRight'>${newPlan?.price}</div>
              </div>

              <div className='downgradeModal__currentPlanDue'>
                <div className='downgradeModal__currentPlanLeft'>
                  <h4 className='downgradeModal__currentPlanDueTitle'>Amount Due today</h4>
                </div>
                <div className='downgradeModal__currentPlanRight'>$00.00</div>
              </div>

              {rolloverMode === CreditsRolloverModesEnum.DOWNGRADE_WITH_ROLLOVER &&
              <div className={styles.rolloverInfoContainer}>
                <span>Your Credits Bank limit will be reduced to {creditsBankMaxCounter(newPlan?.credits)} Credits.</span>
                <span>Your current Credits Bank balance will not be affected<br />(you will not lose any stored Credits).</span>
              </div>}

              {rolloverMode === CreditsRolloverModesEnum.DOWNGRADE_WITHOUT_ROLLOVER &&
              <div className={styles.rolloverInfoContainer}>
                <span>You are downgrading to a plan which does not have Credits Rollover.</span>
                <span>Your current Credits Bank balance will not be affected (you will not lose any stored Credits). However, you will no longer be able to rollover Credits and store them for future use. Are you sure you wish to downgrade?</span>
              </div>}

              <div className='downgradeModal__currentPlanActions'>
                <a className={styles.confirmButton} onClick={downgradeStripePlan}>
                  Confirm
                </a>
                <button className='downgradeModal__currentPlanActionsCancel' onClick={closePlanChangeModal}>
                  Go Back
                </button>

                <p>
                  By confirming your new plan, you agree to Musiverse.Ida's{' '}
                  <a target='_blank' href='https://www.musiversal.com/terms-of-use' rel='noreferrer'>
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a target='_blank' href='https://www.musiversal.com/privacy-policy' rel='noreferrer'>
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </>
          </div>
        )}

        {modalType === 'credits' && (
          <div className='downgradeModal'>
            <h1 className='downgradeModal__title'>Confirm your new plan</h1>

            <>
              <p className='downgradeModal__currentHeading'>Changing To</p>

              <div className='downgradeModal__currentPlan'>
                <div className='downgradeModal__currentPlanLeft'>
                  <h4 className='downgradeModal__currentPlanTitle'> {newPlan.title}</h4>
                  <p className='downgradeModal__currentPlanDescription'>
                    What you'll pay {newPlan?.isAnnual ? 'yearly' : 'monthly'} starting today
                  </p>
                </div>
                <div className='downgradeModal__currentPlanRight'>${newPlan.price}</div>
              </div>

              <div className='downgradeModal__currentPlanDue'>
                <div className='downgradeModal__currentPlanLeft'>
                  <h4 className='downgradeModal__currentPlanDueTitle'>
                    On your next billing date, you will be credited an amount corresponding to the time left on your
                    previous unlimited plan.
                  </h4>
                  <h5 className={styles.switchPlanExplanation}>
                    For example, if you had 15 days remaining on a $99 unlimited plan, you’ll be credited $44.50 on your
                    next billing date.
                  </h5>
                </div>
              </div>

              <div className='downgradeModal__currentPlanActions'>
                <a
                  className='downgradeModal__currentPlanActionsConfirm'
                  onClick={() => redirectToPlanUrl(getPlanPriceIdByEnvHelper(newPlan, paymentsProvider))}
                >
                  Confirm
                </a>
                <button className='downgradeModal__currentPlanActionsCancel' onClick={closePlanChangeModal}>
                  Go Back
                </button>

                <p>
                  By confirming your new plan, you agree to Musiverse.Ida's{' '}
                  <a target='_blank' href='https://www.musiversal.com/terms-of-use' rel='noreferrer'>
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a target='_blank' href='https://www.musiversal.com/privacy-policy' rel='noreferrer'>
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </>
          </div>
        )}
      </PlanChangeModal>

      {/*SELECT PAYMENT MODAL*/}
      <ModalWrapper isOpen={isPaymentMethodModalShown} onRequestClose={() => setIsPaymentMethodModalShown(false)}>
        <div className={styles.paymentMethodModal}>
          <h1>Select Payment Method:</h1>
          <div className={styles.methods}>
            {paymentMethodsData.map(({ name, icon, provider }) =>
              <div
                key={name}
                onClick={selectPaymentMethod(provider)}
                className={classnames(styles.method, provider === paymentMethod && styles.methodSelected)}>
                <p>{name}</p>
                <Icon name={icon} style={{ transform: 'scale(1.75)' }} />
              </div>
            )}
          </div>
          <button
            onClick={joinFromModal}
            className={styles.joinButton}>
            {PlansCtaTypeEnum.NEW} {newPlan?.title}
          </button>
        </div>
      </ModalWrapper>

      {/*ROLLOVER WARNING MODAL*/}
      <ModalWrapper isOpen={isCreditsBankWarningShown} onRequestClose={() => setIsCreditsBankWarningShown(false)}>
        <div className={styles.rolloverWarningModal}>
          {rolloverMode === CreditsRolloverModesEnum.SWITCH && <>
            <h1>Are you sure you wish to switch?</h1>
            <div className={styles.rolloverInfoContainer}>
              <span>You are switching to a plan which does not have Credits Rollover.</span>
              <span>Your current Credits Bank balance will not be affected (you will not lose any stored Credits). However, you will no longer be able to rollover Credits and store them for future use. Are you sure you wish to switch?</span>
            </div>
          </>}
          {rolloverMode === CreditsRolloverModesEnum.DOWNGRADE_WITH_ROLLOVER && <>
            <h1>Are you sure you wish to downgrade?</h1>
            <div className={styles.rolloverInfoContainer}>
              <span>Your Credits Bank limit will be reduced to {creditsBankMaxCounter(newPlan?.credits)} Credits.</span>
              <span>Your current Credits Bank balance will not be affected<br />(you will not lose any stored Credits).</span>
            </div>
          </>}
          {rolloverMode === CreditsRolloverModesEnum.DOWNGRADE_WITHOUT_ROLLOVER && <>
            <h1>Are you sure you wish to downgrade?</h1>
            <div className={styles.rolloverInfoContainer}>
              <span>You are downgrading to a plan which does not have Credits Rollover.</span>
              <span>Your current Credits Bank balance will not be affected (you will not lose any stored Credits). However, you will no longer be able to rollover Credits and store them for future use. Are you sure you wish to downgrade?</span>
            </div>
          </>}
          <button
            onClick={() => rolloverMode === CreditsRolloverModesEnum.SWITCH ? upgradePlan(newPlan) : downgradePlan(newPlan)}
            className={styles.joinButton}>
            {rolloverMode === CreditsRolloverModesEnum.SWITCH ? PlansCtaTypeEnum.SWITCH : PlansCtaTypeEnum.DOWNGRADE} {newPlan?.title}
          </button>
        </div>
      </ModalWrapper>
    </>);
};

export default SubscriptionPlanList;
